export type TymewearTimeParseMethod =
  | "elapsed_seconds_numeric"
  | "clock_hh_mm_ss"
  | "clock_mm_ss"
  | "absolute_datetime"
  | "excel_fractional_day"
  | "excel_serial_datetime"
  | "invalid";

export type TymewearTimeParseConfidence = "high" | "medium" | "low";

export type ParsedTymewearTimePoint = {
  raw: string | null;
  elapsedSeconds: number | null;
  absoluteTimestampMs: number | null;
  parseMethod: TymewearTimeParseMethod;
  parseConfidence: TymewearTimeParseConfidence;
};

export type TymewearTimeSeriesStats = {
  detectedFormat: TymewearTimeParseMethod;
  firstTenRawValues: Array<string | null>;
  firstTenParsedTypes: TymewearTimeParseMethod[];
  minRawNumericValue: number | null;
  maxRawNumericValue: number | null;
  monotonicNonDecreasing: boolean;
  medianDeltaSeconds: number | null;
  validValueCount: number;
  duplicateValueCount: number;
  decreasingValueCount: number;
  gapCount: number;
  irregularSampling: boolean;
};

export type ParsedTymewearTimeSeries = {
  points: ParsedTymewearTimePoint[];
  stats: TymewearTimeSeriesStats;
  firstValidElapsedSeconds: number | null;
  lastValidElapsedSeconds: number | null;
  durationSeconds: number | null;
  sampleIntervalSeconds: number | null;
  parseConfidence: TymewearTimeParseConfidence;
};

type CandidateMethod = Exclude<TymewearTimeParseMethod, "invalid">;

const CLOCK_REGEX = /^(\d{1,2}):(\d{2}(?:\.\d+)?)(?::(\d{2})(\.\d+)?)?$/;

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  }
  return sorted[middle];
}

function parseClock(raw: string): { seconds: number; method: CandidateMethod } | null {
  const match = raw.trim().match(CLOCK_REGEX);
  if (!match) return null;
  const first = Number(match[1]);
  const secondWithFraction = Number(match[2]);
  const third = match[3] ? Number(match[3]) : null;
  const fraction = match[4] ? Number(match[4]) : 0;
  if (!Number.isFinite(first) || !Number.isFinite(secondWithFraction)) return null;
  if (secondWithFraction >= 60) return null;
  if (third !== null && (!Number.isFinite(third) || third >= 60)) return null;
  if (third !== null && !Number.isInteger(secondWithFraction)) return null;
  if (third === null) {
    return { seconds: first * 60 + secondWithFraction, method: "clock_mm_ss" };
  }
  return { seconds: first * 3600 + secondWithFraction * 60 + third + fraction, method: "clock_hh_mm_ss" };
}

function isDateLikeString(raw: string): boolean {
  return /(\d{4}-\d{1,2}-\d{1,2})|(\d{1,2}\/\d{1,2}\/\d{2,4})|([a-z]{3,9}\s+\d{1,2})/i.test(raw) || raw.includes("T");
}

function parseAbsoluteDate(raw: string): number | null {
  if (!isDateLikeString(raw)) return null;
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? null : parsed;
}

function parseNumber(raw: string): number | null {
  const parsed = Number(raw.trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function excelSerialToTimestampMs(serial: number): number {
  return Math.round((serial - 25569) * 86_400_000);
}

function chooseMethod(rawValues: Array<string | null>): CandidateMethod | null {
  let numericCount = 0;
  let absoluteDateCount = 0;
  let clockCount = 0;
  let mmssCount = 0;
  let hhmmssCount = 0;
  let fractionalDayCount = 0;
  let serialDateCount = 0;

  for (const rawValue of rawValues) {
    if (!rawValue) continue;
    const raw = rawValue.trim();
    if (!raw) continue;

    const absolute = parseAbsoluteDate(raw);
    if (absolute !== null) {
      absoluteDateCount += 1;
      continue;
    }

    const clock = parseClock(raw);
    if (clock) {
      clockCount += 1;
      if (clock.method === "clock_mm_ss") mmssCount += 1;
      if (clock.method === "clock_hh_mm_ss") hhmmssCount += 1;
      continue;
    }

    const numeric = parseNumber(raw);
    if (numeric !== null) {
      numericCount += 1;
      if (numeric >= 0 && numeric < 1.5) fractionalDayCount += 1;
      if (numeric >= 25_000 && numeric < 70_000) serialDateCount += 1;
    }
  }

  const total = rawValues.filter(Boolean).length;
  if (!total) return null;

  if (absoluteDateCount / total >= 0.6) return "absolute_datetime";
  if (clockCount / total >= 0.6) return hhmmssCount >= mmssCount ? "clock_hh_mm_ss" : "clock_mm_ss";
  if (numericCount / total >= 0.6) {
    if (fractionalDayCount / Math.max(1, numericCount) >= 0.6) return "excel_fractional_day";
    if (serialDateCount / Math.max(1, numericCount) >= 0.6) return "excel_serial_datetime";
    return "elapsed_seconds_numeric";
  }

  return null;
}

function parseByMethod(raw: string | null, method: CandidateMethod): ParsedTymewearTimePoint {
  const defaultPoint: ParsedTymewearTimePoint = {
    raw,
    elapsedSeconds: null,
    absoluteTimestampMs: null,
    parseMethod: "invalid",
    parseConfidence: "low"
  };
  if (!raw || !raw.trim()) return defaultPoint;

  if (method === "absolute_datetime") {
    const timestampMs = parseAbsoluteDate(raw);
    if (timestampMs === null) return defaultPoint;
    return {
      raw,
      elapsedSeconds: null,
      absoluteTimestampMs: timestampMs,
      parseMethod: method,
      parseConfidence: "high"
    };
  }

  if (method === "clock_hh_mm_ss" || method === "clock_mm_ss") {
    const clock = parseClock(raw);
    if (!clock) return defaultPoint;
    return {
      raw,
      elapsedSeconds: clock.seconds,
      absoluteTimestampMs: null,
      parseMethod: clock.method,
      parseConfidence: "high"
    };
  }

  const numeric = parseNumber(raw);
  if (numeric === null || numeric < 0) return defaultPoint;

  if (method === "excel_fractional_day") {
    return {
      raw,
      elapsedSeconds: numeric * 86400,
      absoluteTimestampMs: null,
      parseMethod: method,
      parseConfidence: "medium"
    };
  }

  if (method === "excel_serial_datetime") {
    const absoluteTimestampMs = excelSerialToTimestampMs(numeric);
    return {
      raw,
      elapsedSeconds: null,
      absoluteTimestampMs,
      parseMethod: method,
      parseConfidence: "medium"
    };
  }

  return {
    raw,
    elapsedSeconds: numeric,
    absoluteTimestampMs: null,
    parseMethod: method,
    parseConfidence: "high"
  };
}

export function parseTymewearTimeSeries(rawValues: Array<string | null>): ParsedTymewearTimeSeries {
  const method = chooseMethod(rawValues);
  const points = rawValues.map(raw => {
    if (!method) {
      return {
        raw,
        elapsedSeconds: null,
        absoluteTimestampMs: null,
        parseMethod: "invalid" as const,
        parseConfidence: "low" as const
      };
    }
    return parseByMethod(raw, method);
  });

  const firstAbsolute = points.find(point => point.absoluteTimestampMs !== null)?.absoluteTimestampMs ?? null;
  if (firstAbsolute !== null) {
    points.forEach(point => {
      if (point.absoluteTimestampMs !== null) {
        point.elapsedSeconds = (point.absoluteTimestampMs - firstAbsolute) / 1000;
      }
    });
  }

  const validElapsed = points
    .map(point => point.elapsedSeconds)
    .filter((value): value is number => value !== null && Number.isFinite(value));

  const deltas: number[] = [];
  let duplicateValueCount = 0;
  let decreasingValueCount = 0;
  for (let index = 1; index < points.length; index += 1) {
    const prev = points[index - 1].elapsedSeconds;
    const current = points[index].elapsedSeconds;
    if (prev === null || current === null) continue;
    const delta = current - prev;
    if (delta === 0) duplicateValueCount += 1;
    if (delta < 0) decreasingValueCount += 1;
    if (delta > 0) deltas.push(delta);
  }

  const sampleIntervalSeconds = median(deltas);
  const durationSeconds = validElapsed.length >= 2
    ? Math.max(0, validElapsed[validElapsed.length - 1] - validElapsed[0])
    : null;
  const monotonicNonDecreasing = decreasingValueCount === 0;
  const gapCount = sampleIntervalSeconds === null
    ? 0
    : deltas.filter(delta => delta > sampleIntervalSeconds * 3).length;
  const irregularSampling = sampleIntervalSeconds === null
    ? false
    : deltas.some(delta => Math.abs(delta - sampleIntervalSeconds) > Math.max(1, sampleIntervalSeconds * 0.5));

  const parseConfidence: TymewearTimeParseConfidence = validElapsed.length >= Math.max(50, Math.round(rawValues.length * 0.7)) && monotonicNonDecreasing
    ? "high"
    : validElapsed.length >= Math.max(10, Math.round(rawValues.length * 0.4))
      ? "medium"
      : "low";

  const numericRawValues = rawValues
    .map(raw => (raw ? parseNumber(raw) : null))
    .filter((value): value is number => value !== null);

  const stats: TymewearTimeSeriesStats = {
    detectedFormat: method ?? "invalid",
    firstTenRawValues: rawValues.slice(0, 10),
    firstTenParsedTypes: points.slice(0, 10).map(point => point.parseMethod),
    minRawNumericValue: numericRawValues.length ? Math.min(...numericRawValues) : null,
    maxRawNumericValue: numericRawValues.length ? Math.max(...numericRawValues) : null,
    monotonicNonDecreasing,
    medianDeltaSeconds: sampleIntervalSeconds,
    validValueCount: validElapsed.length,
    duplicateValueCount,
    decreasingValueCount,
    gapCount,
    irregularSampling
  };

  return {
    points,
    stats,
    firstValidElapsedSeconds: validElapsed[0] ?? null,
    lastValidElapsedSeconds: validElapsed.length ? validElapsed[validElapsed.length - 1] : null,
    durationSeconds,
    sampleIntervalSeconds,
    parseConfidence
  };
}
